import type { ProductMetadata } from "../types/productMetadata";
import type { Visibility } from "../types/subscription";
import {
  buildPaginationQuery,
  type V3ApiResponse,
  type PaginationParams,
  type PaginatedResponse,
  type BasketDetail,
  type BasketListItem,
  type BasketContractState,
  type BasketCompositionRequest,
  type PublishBasketResult,
  type PatchBasketRequest,
  type PortfolioDetail,
  type PortfolioContractState,
  type PortfolioBasketState,
  type PortfolioCompositionRequest,
  type PublishPortfolioResult,
  type PatchPortfolioRequest,
  type AuditLogEntry,
  type PortfolioUsageItem,
  type PortfolioListItem,
  type BasketActiveComposition,
  type BasketAfterActivationResult,
  type BasketCompositionChangeEntry,
  type PaginatedCompositionChanges,
  type PortfolioActiveComposition,
  type PortfolioAfterActivationResult,
  type PortfolioCompositionChangeEntry,
  type VersionListItem,
  type VersionCommentMeta,
  type VersionMetadataItem,
  type CreateVersionMetadataRequest,
} from "../types/productV3";
import type {
  PendingContractsData,
  BasketPendingContract,
  PortfolioPendingContract,
  CancelPendingActivationsResult,
  LockStatusResult,
} from "../types/pendingContracts";
import type { ProductShare } from "./productShareService";
import httpService, { HttpError } from "./httpService";

const BASE = "/v3/products/providers";

class AdminProductsV3Service {
  async listBaskets(
    providerId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<BasketListItem>> {
    const query = buildPaginationQuery(params);
    const response = await httpService.get<PaginatedResponse<BasketListItem>>(
      `${BASE}/${providerId}/baskets${query}`
    );
    return response.data;
  }

  async listPortfolios(
    providerId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<PortfolioListItem>> {
    const query = buildPaginationQuery(params);
    const response = await httpService.get<PaginatedResponse<PortfolioListItem>>(
      `${BASE}/${providerId}/portfolios${query}`
    );
    return response.data;
  }

  async listAllBaskets(providerId: number): Promise<BasketListItem[]> {
    const all: BasketListItem[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await this.listBaskets(providerId, { page, pageSize: 100 });
      all.push(...response.items);
      totalPages = response.totalPages;
      page += 1;
    } while (page <= totalPages);

    return all;
  }

  async listAllPortfolios(providerId: number): Promise<PortfolioListItem[]> {
    const all: PortfolioListItem[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await this.listPortfolios(providerId, { page, pageSize: 100 });
      all.push(...response.items);
      totalPages = response.totalPages;
      page += 1;
    } while (page <= totalPages);

    return all;
  }

  async getBasketDetail(providerId: number, basketId: number): Promise<BasketDetail> {
    const response = await httpService.get<V3ApiResponse<BasketDetail>>(
      `${BASE}/${providerId}/baskets/${basketId}`
    );
    return response.data.data;
  }

  async getPortfolioDetail(providerId: number, portfolioId: number): Promise<PortfolioDetail> {
    const response = await httpService.get<V3ApiResponse<PortfolioDetail>>(
      `${BASE}/${providerId}/portfolios/${portfolioId}`
    );
    return response.data.data;
  }

  async patchBasketMetadata(
    providerId: number,
    basketId: number,
    metadata: ProductMetadata | null
  ): Promise<BasketDetail> {
    const body: PatchBasketRequest = { metadata: metadata as Record<string, unknown> | null };
    const response = await httpService.patch<V3ApiResponse<BasketDetail>>(
      `${BASE}/${providerId}/baskets/${basketId}`,
      body
    );
    return response.data.data;
  }

  async patchPortfolioMetadata(
    providerId: number,
    portfolioId: number,
    metadata: ProductMetadata | null
  ): Promise<PortfolioDetail> {
    const body: PatchPortfolioRequest = { metadata: metadata as Record<string, unknown> | null };
    const response = await httpService.patch<V3ApiResponse<PortfolioDetail>>(
      `${BASE}/${providerId}/portfolios/${portfolioId}`,
      body
    );
    return response.data.data;
  }

  async patchProductMetadata(
    productType: "basket" | "portfolio" | "managedStrategy",
    providerId: number | null,
    productId: number,
    metadata: ProductMetadata | null
  ): Promise<void> {
    if (productType === "basket") {
      await this.patchBasketMetadata(providerId as number, productId, metadata);
    } else if (productType === "portfolio") {
      await this.patchPortfolioMetadata(providerId as number, productId, metadata);
    } else {
      await httpService.put<V3ApiResponse<unknown>>(
        `/v3/admin/managed-strategies/${productId}`,
        { metadata: metadata as Record<string, unknown> | null }
      );
    }
  }

  async patchProductVisibility(
    productType: "basket" | "portfolio" | "managedStrategy",
    providerId: number | null,
    productId: number,
    visibility: Visibility
  ): Promise<void> {
    if (productType === "basket") {
      const body: PatchBasketRequest = { visibility };
      await httpService.patch<V3ApiResponse<BasketDetail>>(
        `${BASE}/${providerId}/baskets/${productId}`,
        body
      );
    } else if (productType === "portfolio") {
      const body: PatchPortfolioRequest = { visibility };
      await httpService.patch<V3ApiResponse<PortfolioDetail>>(
        `${BASE}/${providerId}/portfolios/${productId}`,
        body
      );
    } else {
      await httpService.put<V3ApiResponse<unknown>>(
        `/v3/admin/managed-strategies/${productId}`,
        { visibility }
      );
    }
  }

  // PWA-2368: set / change / clear a product's rate-card binding. A positive
  // `rateCardId` binds; explicit `null` clears (falls back to provider/default).
  // The key is always present in the body, so `null` is sent through verbatim.
  async patchProductRateCard(
    productType: "basket" | "portfolio",
    providerId: number,
    productId: number,
    rateCardId: number | null
  ): Promise<void> {
    const path = productType === "basket" ? "baskets" : "portfolios";
    const body: PatchBasketRequest = { rateCardId };
    await httpService.patch<V3ApiResponse<BasketDetail | PortfolioDetail>>(
      `${BASE}/${providerId}/${path}/${productId}`,
      body
    );
  }

}

export const adminProductsV3Service = new AdminProductsV3Service();

// ---------------------------------------------------------------------------
// Admin runtime service factories for CurationRuntimeContext
// ---------------------------------------------------------------------------

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch {
    return await fn();
  }
}

export function createAdminBasketRuntimeService(providerId: number) {
  const base = `${BASE}/${providerId}/baskets`;

  return {
    createBasket: (): never => {
      throw new Error("Admin curation does not support basket creation.");
    },

    getBaskets: async (params?: PaginationParams) => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<BasketListItem>>(
        `${base}${query}`
      );
      return response.data;
    },

    getBasket: async (basketId: number): Promise<BasketDetail> => {
      const response = await httpService.get<V3ApiResponse<BasketDetail>>(
        `${base}/${basketId}`
      );
      return response.data.data;
    },

    getContractStates: async (basketId: number): Promise<BasketContractState[]> => {
      const response = await httpService.get<V3ApiResponse<BasketContractState[]>>(
        `${base}/${basketId}/contract-states`
      );
      return response.data.data;
    },

    getAfterActivation: (basketId: number): Promise<BasketAfterActivationResult> =>
      withRetry(async () => {
        const response = await httpService.get<V3ApiResponse<BasketAfterActivationResult>>(
          `${base}/${basketId}/after-activation`
        );
        return response.data.data;
      }),

    getActiveComposition: (basketId: number): Promise<BasketActiveComposition> =>
      withRetry(async () => {
        const response = await httpService.get<V3ApiResponse<BasketActiveComposition>>(
          `${base}/${basketId}/active`
        );
        return response.data.data;
      }),

    getChanges: (basketId: number): Promise<PaginatedCompositionChanges<BasketCompositionChangeEntry>> =>
      withRetry(async () => {
        const response = await httpService.get<
          V3ApiResponse<PaginatedCompositionChanges<BasketCompositionChangeEntry>>
        >(`${base}/${basketId}/changes`);
        return response.data.data;
      }),

    getPendingContracts: async (basketId: number): Promise<PendingContractsData<BasketPendingContract>> => {
      const response = await httpService.get<
        V3ApiResponse<PendingContractsData<BasketPendingContract>>
      >(`${base}/${basketId}/pending-contracts`);
      return response.data.data;
    },

    cancelPendingActivations: async (basketId: number): Promise<CancelPendingActivationsResult> => {
      const response = await httpService.post<V3ApiResponse<CancelPendingActivationsResult>>(
        `${base}/${basketId}/cancel-pending-activations`
      );
      return response.data.data;
    },

    getLockStatus: async (basketId: number): Promise<LockStatusResult> => {
      const response = await httpService.get<LockStatusResult>(
        `${base}/${basketId}/lock-status`
      );
      return response.data;
    },

    getPortfolioUsage: async (basketId: number): Promise<PortfolioUsageItem[] | null> => {
      try {
        const response = await httpService.get<V3ApiResponse<PortfolioUsageItem[]>>(
          `${base}/${basketId}/portfolio-usage`
        );
        return response.data.data;
      } catch (error) {
        if ((error as HttpError).status === 404) return null;
        throw error;
      }
    },

    updateComposition: async (basketId: number, data: BasketCompositionRequest): Promise<BasketDetail> => {
      const response = await httpService.put<V3ApiResponse<BasketDetail>>(
        `${base}/${basketId}/composition`,
        data
      );
      return response.data.data;
    },

    publishBasket: async (basketId: number): Promise<PublishBasketResult> => {
      const response = await httpService.post<V3ApiResponse<PublishBasketResult>>(
        `${base}/${basketId}/publish`
      );
      return response.data.data;
    },

    patchBasket: async (basketId: number, data: PatchBasketRequest): Promise<BasketDetail> => {
      const response = await httpService.patch<V3ApiResponse<BasketDetail>>(
        `${base}/${basketId}`,
        data
      );
      return response.data.data;
    },

    deleteBasket: async (basketId: number): Promise<void> => {
      await httpService.delete(`${base}/${basketId}`);
    },

    getVersions: async (basketId: number): Promise<VersionListItem[]> => {
      const response = await httpService.get<V3ApiResponse<VersionListItem[]>>(
        `${base}/${basketId}/versions`
      );
      return response.data.data;
    },

    createVersionMetadata: async (
      basketId: number,
      versionId: number,
      metadata: VersionCommentMeta
    ): Promise<VersionMetadataItem> => {
      const body: CreateVersionMetadataRequest = { metadata };
      const response = await httpService.post<V3ApiResponse<VersionMetadataItem>>(
        `${base}/${basketId}/versions/${versionId}/metadata`,
        body
      );
      return response.data.data;
    },

    getVersionMetadata: async (
      basketId: number,
      versionId: number,
      params?: PaginationParams
    ): Promise<PaginatedResponse<VersionMetadataItem>> => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${base}/${basketId}/versions/${versionId}/metadata${query}`
      );
      return response.data;
    },

    deleteVersionMetadata: async (
      basketId: number,
      versionId: number,
      metadataId: number
    ): Promise<void> => {
      await httpService.delete(
        `${base}/${basketId}/versions/${versionId}/metadata/${metadataId}`
      );
    },

    getAllVersionMetadata: async (
      basketId: number,
      params?: PaginationParams
    ): Promise<PaginatedResponse<VersionMetadataItem>> => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${base}/${basketId}/version-metadata${query}`
      );
      return response.data;
    },
  };
}

export function createAdminPortfolioRuntimeService(providerId: number) {
  const base = `${BASE}/${providerId}/portfolios`;

  return {
    createPortfolio: (): never => {
      throw new Error("Admin curation does not support portfolio creation.");
    },

    getPortfolio: async (portfolioId: number): Promise<PortfolioDetail> => {
      const response = await httpService.get<V3ApiResponse<PortfolioDetail>>(
        `${base}/${portfolioId}`
      );
      return response.data.data;
    },

    getContractStates: async (portfolioId: number): Promise<PortfolioContractState[]> => {
      const response = await httpService.get<V3ApiResponse<PortfolioContractState[]>>(
        `${base}/${portfolioId}/contract-states`
      );
      return response.data.data;
    },

    getBasketStates: async (portfolioId: number): Promise<PortfolioBasketState[] | null> => {
      try {
        const response = await httpService.get<V3ApiResponse<PortfolioBasketState[]>>(
          `${base}/${portfolioId}/basket-states`
        );
        return response.data.data;
      } catch (error) {
        if ((error as HttpError).status === 404) return null;
        throw error;
      }
    },

    getAfterActivation: (portfolioId: number): Promise<PortfolioAfterActivationResult> =>
      withRetry(async () => {
        const response = await httpService.get<V3ApiResponse<PortfolioAfterActivationResult>>(
          `${base}/${portfolioId}/after-activation`
        );
        return response.data.data;
      }),

    getActiveComposition: (portfolioId: number): Promise<PortfolioActiveComposition> =>
      withRetry(async () => {
        const response = await httpService.get<V3ApiResponse<PortfolioActiveComposition>>(
          `${base}/${portfolioId}/active`
        );
        return response.data.data;
      }),

    getChanges: (portfolioId: number): Promise<PaginatedCompositionChanges<PortfolioCompositionChangeEntry>> =>
      withRetry(async () => {
        const response = await httpService.get<
          V3ApiResponse<PaginatedCompositionChanges<PortfolioCompositionChangeEntry>>
        >(`${base}/${portfolioId}/changes`);
        return response.data.data;
      }),

    getPendingContracts: async (portfolioId: number): Promise<PendingContractsData<PortfolioPendingContract>> => {
      const response = await httpService.get<
        V3ApiResponse<PendingContractsData<PortfolioPendingContract>>
      >(`${base}/${portfolioId}/pending-contracts`);
      return response.data.data;
    },

    cancelPendingActivations: async (portfolioId: number): Promise<CancelPendingActivationsResult> => {
      const response = await httpService.post<V3ApiResponse<CancelPendingActivationsResult>>(
        `${base}/${portfolioId}/cancel-pending-activations`
      );
      return response.data.data;
    },

    getLockStatus: async (portfolioId: number): Promise<LockStatusResult> => {
      const response = await httpService.get<LockStatusResult>(
        `${base}/${portfolioId}/lock-status`
      );
      return response.data;
    },

    getAuditLog: async (portfolioId: number, params?: PaginationParams): Promise<PaginatedResponse<AuditLogEntry>> => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<V3ApiResponse<PaginatedResponse<AuditLogEntry>>>(
        `${base}/${portfolioId}/user-updates${query}`
      );
      return response.data.data;
    },

    updateComposition: async (portfolioId: number, data: PortfolioCompositionRequest): Promise<PortfolioDetail> => {
      const response = await httpService.put<V3ApiResponse<PortfolioDetail>>(
        `${base}/${portfolioId}/composition`,
        data
      );
      return response.data.data;
    },

    publishPortfolio: async (portfolioId: number): Promise<PublishPortfolioResult> => {
      const response = await httpService.post<V3ApiResponse<PublishPortfolioResult>>(
        `${base}/${portfolioId}/publish`
      );
      return response.data.data;
    },

    patchPortfolio: async (portfolioId: number, data: PatchPortfolioRequest): Promise<PortfolioDetail> => {
      const response = await httpService.patch<V3ApiResponse<PortfolioDetail>>(
        `${base}/${portfolioId}`,
        data
      );
      return response.data.data;
    },

    deletePortfolio: async (portfolioId: number): Promise<void> => {
      await httpService.delete(`${base}/${portfolioId}`);
    },

    getVersions: async (portfolioId: number): Promise<VersionListItem[]> => {
      const response = await httpService.get<V3ApiResponse<VersionListItem[]>>(
        `${base}/${portfolioId}/versions`
      );
      return response.data.data;
    },

    createVersionMetadata: async (
      portfolioId: number,
      versionId: number,
      metadata: VersionCommentMeta
    ): Promise<VersionMetadataItem> => {
      const body: CreateVersionMetadataRequest = { metadata };
      const response = await httpService.post<V3ApiResponse<VersionMetadataItem>>(
        `${base}/${portfolioId}/versions/${versionId}/metadata`,
        body
      );
      return response.data.data;
    },

    getVersionMetadata: async (
      portfolioId: number,
      versionId: number,
      params?: PaginationParams
    ): Promise<PaginatedResponse<VersionMetadataItem>> => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${base}/${portfolioId}/versions/${versionId}/metadata${query}`
      );
      return response.data;
    },

    deleteVersionMetadata: async (
      portfolioId: number,
      versionId: number,
      metadataId: number
    ): Promise<void> => {
      await httpService.delete(
        `${base}/${portfolioId}/versions/${versionId}/metadata/${metadataId}`
      );
    },

    getAllVersionMetadata: async (
      portfolioId: number,
      params?: PaginationParams
    ): Promise<PaginatedResponse<VersionMetadataItem>> => {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${base}/${portfolioId}/version-metadata${query}`
      );
      return response.data;
    },
  };
}

export function createAdminShareLookupService(providerId: number) {
  return {
    getShare: async (
      productType: "BASKET" | "PORTFOLIO",
      productId: number
    ): Promise<ProductShare | null> => {
      try {
        const response = await httpService.get<{ data: ProductShare }>(
          `${BASE}/${providerId}/shares/${productType}/${productId}`
        );
        return response.data.data;
      } catch (error: any) {
        if (error?.response?.status === 404 || (error as HttpError).status === 404) return null;
        throw error;
      }
    },
  };
}
